import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProductCategoriesService } from '../../services/product-categories.service';
import { InputFieldComponent } from '../../../../../shared/src/lib/components/input-field/input-field.component';
import { ActivatedRoute, Router } from '@angular/router';
import { DynamicRouteHandlingService } from 'libs/shared/src/lib/services/dynamic-route-handling.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileDropComponent, FileUploadService, GalleryImage, rxResourceFixed } from '@zayan-erp/shared';
import { of, tap, switchMap, map } from 'rxjs';
import { GenericDropdownComponent } from '../../../../../shared/src/lib/components/generic-dropdown/generic-dropdown.component';
import { IProductCategoryByIdResponseData, ProductCategory } from '../../models/product.model';
import { selectProductList } from '@zayan-erp/stores';
import { Store } from '@ngrx/store';
import { AppState } from 'apps/cinari-erp-client/src/app/store/app.reducers';
import { AsyncPipe } from '@angular/common';
import * as HomebuildingActions from '@zayan-erp/stores';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { HttpEventType } from '@angular/common/http';

type CategoryImageState = GalleryImage & { progress: number | null; s3Key: string; documentId: string };

@Component({
  selector: 'lib-product-categories-form',
  imports: [
    InputFieldComponent,
    ReactiveFormsModule,
    GenericDropdownComponent,
    FormsModule,
    AsyncPipe,
    FileDropComponent
  ],
  templateUrl: './product-categories-form.component.html'
})
export class ProductCategoriesFormComponent {
  #productCategoriesService = inject(ProductCategoriesService);
  #activatedRoute = inject(ActivatedRoute);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #router = inject(Router);
  #store = inject(Store<AppState>);
  #fileUploadService = inject(FileUploadService);
  #destroyRef = inject(DestroyRef);

  constructor() {
    this.#dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.onSubmit();
    });
  }

  selectedCategoryId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id'));
  selectedProductListId = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('productListId'));

  categoryImage = signal<CategoryImageState | null>(null);
  categoryImageDocumentId = signal<string | null>(null);
  existingImageUrl = signal<string | null>(null);

  onImageFileDrop(files: NgxFileDropEntry[]) {
    const dropped = files[0];
    if (!dropped?.fileEntry.isFile) return;
    (dropped.fileEntry as FileSystemFileEntry).file((file: File) => {
      this.#setImageAndUpload(file);
    });
  }

  onImageFileChange(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.#setImageAndUpload(file);
  }

  #setImageAndUpload(file: File) {
    this.categoryImage.set({
      file,
      fileSrcUrl: URL.createObjectURL(file),
      selected: false,
      progress: null,
      s3Key: '',
      documentId: ''
    });
    this.#uploadCategoryImage(file);
  }

  #uploadCategoryImage(file: File) {
    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3(awsData).pipe(
            tap((uploadEvent: any) => {
              if (uploadEvent?.type === HttpEventType.UploadProgress && uploadEvent.total) {
                const progress = Math.round((100 * uploadEvent.loaded) / uploadEvent.total);
                this.categoryImage.update(img =>
                  img ? { ...img, progress, s3Key: awsData.s3Key, documentId: awsData.documentId } : img
                );
              }
            }),
            map(() => awsData)
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(awsData => {
        this.categoryImageDocumentId.set(awsData.documentId);
        this.categoryImage.update(img =>
          img ? { ...img, progress: 100, s3Key: awsData.s3Key, documentId: awsData.documentId } : img
        );
      });
  }

  categoryForm = new FormGroup({
    productListId: new FormControl<string | null>(this.selectedProductListId() ?? null, Validators.required),
    code: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null, Validators.required),
    imageId: new FormControl<string | null>(null)
  });

  productList$ = this.#store.select(selectProductList);

  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedCategoryId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#productCategoriesService.readProductCategoriesById(params.id).pipe(
          tap(response => {
            this.editForm(response);
          })
        );
      }
      return of(null);
    }
  });

  editForm(productCategory: IProductCategoryByIdResponseData) {
    console.log('Editing category:', productCategory);
    this.categoryForm.patchValue({
      productListId: productCategory.productList.id,
      code: productCategory.code,
      description: productCategory.description,
      imageId: productCategory.image?.id ?? null
    });
    if (productCategory.image?.directUrl) {
      this.existingImageUrl.set(productCategory.image.directUrl);
    }
  }

  onSubmit() {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }
    const productCategory: ProductCategory = {
      id: this.selectedCategoryId() ?? undefined,
      code: this.categoryForm.value.code ?? '',
      description: this.categoryForm.value.description ?? '',
      productListId: this.categoryForm.value.productListId ?? '',
      imageId: this.categoryImageDocumentId() ?? undefined
    };
    if (this.selectedCategoryId()) {
      this.#productCategoriesService.updateProductCategory(productCategory).subscribe({
        next: () => {
          this.categoryForm.reset();
          this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']]);
          this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
          this.#store.dispatch(
            HomebuildingActions.loadCategoryListByProductListId({
              productListId: productCategory.productListId,
              force: true
            })
          );
        }
      });
    } else {
      this.#productCategoriesService.createProductCategory(productCategory).subscribe({
        next: () => {
          this.categoryForm.reset();
          this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']]);
          this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
          this.#store.dispatch(
            HomebuildingActions.loadCategoryListByProductListId({
              productListId: productCategory.productListId,
              force: true
            })
          );
        }
      });
    }
  }
}
